// src/Checklist/ChecklistTasksCard.jsx
import React, { useEffect, useState } from "react";
import { api } from "../api";
import ChecklistFillModal from "./ChecklistFillModal";
import "./ChecklistTasksCard.css";

/*
  selectedId : id of the checklist chosen to attach to the timesheet entry
  onSelect   : (checklist | null) => void
*/
const ChecklistTasksCard = ({
  user,
  refreshKey = 0,
  selectedId = null,
  onSelect,
}) => {
  const [pending, setPending] = useState([]);
  const [toCheck, setToCheck] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [userNames, setUserNames] = useState([]);
  const [modalConfig, setModalConfig] = useState(null);
  const [error, setError] = useState("");

  const authHeader = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };

  const userName = String(user?.name || "").trim();

  const loadLists = async () => {
    try {
      const [p, c] = await Promise.all([
        api.get("/checklists/my?status=pending", authHeader),
        api.get("/checklists/to-check", authHeader),
      ]);
      setPending(Array.isArray(p.data) ? p.data : []);
      setToCheck(Array.isArray(c.data) ? c.data : []);
    } catch (e) {
      console.warn("Failed to load checklist tasks", e);
      setPending([]);
      setToCheck([]);
    }
  };

  // templates, projects and users are needed to open a checklist
  useEffect(() => {
    const init = async () => {
      try {
        const [t, p, u] = await Promise.all([
          api.get("/checklists/templates", authHeader),
          api.get("/projects", authHeader),
          api.get("/users", authHeader),
        ]);
        setTemplates(Array.isArray(t.data) ? t.data : []);
        setProjects(Array.isArray(p.data) ? p.data : []);
        const ulist = Array.isArray(u.data) ? u.data : u.data?.users || [];
        setUserNames(
          [
            ...new Set(
              ulist.map((x) => String(x.name || "").trim()).filter(Boolean),
            ),
          ].sort((a, b) => a.localeCompare(b)),
        );
      } catch (e) {
        console.warn("Checklist card init failed", e);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reload on mount, when the popup closes (refreshKey) and every minute
  useEffect(() => {
    loadLists();
    const timer = setInterval(loadLists, 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const openAssignment = async (a, readOnly) => {
    setError("");
    const tpl = templates.find((t) => t._id === String(a.templateId));
    if (!tpl || !tpl.htmlPath) {
      setError("This checklist template is not available.");
      return;
    }

    // find the project to get its address
    const projectId = String(a.projectId?._id || a.projectId || "");
    const proj = projects.find((p) => String(p._id) === projectId);

    try {
      const res = await api.get(`/checklists/${a._id}`, authHeader);
      setModalConfig({
        mode: "assignment",
        title: a.templateTitle,
        htmlPath: tpl.htmlPath,
        assignmentId: a._id,
        projectName: a.projectName,
        data: res.data?.data || {},
        readOnly,
        defaults: {
          projectName: a.projectName,
          projectAddress: String(proj?.clientAddress || "").trim(),
          preparedBy: userName,
        },
        users: userNames,
      });
    } catch (e) {
      setError("Could not open the checklist.");
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "");

  if (pending.length === 0 && toCheck.length === 0 && !modalConfig) {
    return (
      <div className="card ctc-card">
        <h3 className="ctc-heading">📋 My Pending Checklists</h3>
        <div className="ctc-empty">No pending checklists.</div>
      </div>
    );
  }

  return (
    <div className="card ctc-card">
      <h3 className="ctc-heading">📋 My Pending Checklists</h3>

      {error && <div className="ctc-error">{error}</div>}

      {pending.length === 0 ? (
        <div className="ctc-empty">No pending checklists.</div>
      ) : (
        pending.map((a) => {
          const filled = a.status === "submitted";
          const selected = selectedId === a._id;

          return (
            <div
              key={a._id}
              className={`ctc-item ${selected ? "ctc-item-selected" : ""}`}
            >
              <div className="ctc-item-main">
                <div className="ctc-item-title">
                  {a.templateTitle}
                  {filled && <span className="ctc-badge">Filled</span>}
                </div>
                <div className="ctc-item-meta">
                  {a.projectName} · assigned by {a.assignedBy?.name || "—"} ·{" "}
                  {fmtDate(a.createdAt)}
                </div>

                {filled && (
                  <label className="ctc-attach">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => onSelect && onSelect(selected ? null : a)}
                    />
                    Attach to this entry
                  </label>
                )}
              </div>

              {filled ? (
                <button
                  type="button"
                  className="ctc-btn ctc-btn-alt"
                  onClick={() => openAssignment(a, true)}
                >
                  View
                </button>
              ) : (
                <button
                  type="button"
                  className="ctc-btn"
                  onClick={() => openAssignment(a, false)}
                >
                  Fill
                </button>
              )}
            </div>
          );
        })
      )}

      {toCheck.length > 0 && (
        <>
          <h4 className="ctc-sub">To Check</h4>
          {toCheck.map((a) => (
            <div key={a._id} className="ctc-item ctc-item-check">
              <div className="ctc-item-main">
                <div className="ctc-item-title">{a.templateTitle}</div>
                <div className="ctc-item-meta">
                  {a.projectName} · filled by {a.assignedTo?.name || "—"} ·{" "}
                  {fmtDate(a.submittedAt)}
                </div>
              </div>
              <button
                type="button"
                className="ctc-btn ctc-btn-alt"
                onClick={() => openAssignment(a, true)}
              >
                View
              </button>
            </div>
          ))}
        </>
      )}

      {modalConfig && (
        <ChecklistFillModal
          config={modalConfig}
          onClose={() => setModalConfig(null)}
          onSaved={() => loadLists()}
        />
      )}
    </div>
  );
};

export default ChecklistTasksCard;
